// AddReviewEntity.tsx
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    SelectChangeEvent,
    Box,
    Chip,
    Typography,
    Divider
} from '@mui/material';
import { getReviewEntityTypes, getUsers, createReviewEntity, getMyReviewEntities, getShowOtherUsersReviewEntities, getReviewEntityByReviewee, getReviewDefinitions, type ReviewDefinition } from '../../services/api';
import { getUser } from '../../services/auth';

interface AddReviewEntityProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface FormData {
    name: string;
    description: string;
    org_entity_code: string;
    reviewers: number[];
    reviewees: number[];
    data_entry_supports: number[];
    owner?: number;
    status: string;
    valid_from: string;
    valid_till: string;
    review_entity_type_id: number;
    parent_ids: number[];
    review_definition_ids: number[];
}

interface EntityTypeByCategory {
    [category: string]: {
        id: number;
        name: string;
        display_label: string;
        description: string;
    }[];
}

interface ReviewEntity {
    id: number;
    name: string;
    description: string;
    review_entity_type_id: number;
    review_entity_type?: {
        display_label: string;
    };
}

export const AddReviewEntity: React.FC<AddReviewEntityProps> = ({ open, onClose, onSuccess }) => {
    const [error, setError] = useState<string>('');
    const [users, setUsers] = useState<any[]>([]);
    const [entityTypes, setEntityTypes] = useState<any[]>([]);
    const [entityTypesByCategory, setEntityTypesByCategory] = useState<EntityTypeByCategory>({});
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [availableEntities, setAvailableEntities] = useState<any[]>([]);
    const [reviewEntities, setReviewEntities] = useState<ReviewEntity[]>([]);
    const [userId, setUserId] = useState<number | null>(null);
    const [showOtherUsersReviewEntities, setShowOtherUsersReviewEntities] = useState<boolean>(true);
    const [allReviewDefinitions, setAllReviewDefinitions] = useState<ReviewDefinition[]>([]);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        description: '',
        org_entity_code: '',
        reviewers: [],
        reviewees: [],
        data_entry_supports: [],
        owner: undefined,
        status: 'ACTIVE',
        valid_from: new Date().toISOString().split('T')[0], // Current date
        valid_till: '9999-12-31', // Infinity date
        review_entity_type_id: 0,
        parent_ids: [],
        review_definition_ids: []
    });

    const fetchUsers = async () => {
        try {
            const response = await getUsers();
            setUsers(response.data);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
        }
    };


    const fetchEntityTypes = async () => {
        try {
            const response = await getReviewEntityTypes();
            setEntityTypes(response.data);
            
            // Group entity types by category
            const groupedByCategory: EntityTypeByCategory = {};
            response.data.forEach((type: any) => {
                if (!groupedByCategory[type.category]) {
                    groupedByCategory[type.category] = [];
                }
                groupedByCategory[type.category].push(type);
            });
            
            setEntityTypesByCategory(groupedByCategory);
        } catch (err) {
            console.error('Error fetching entity types:', err);
            setError('Failed to load entity types');
        }
    };
    
    const fetchReviewEntities = async () => {
        if (!userId) return;
        
        try {
            // First get the config setting
            console.log('DEBUG: AddReviewEntity - Fetching config to determine which API to use');
            const configResponse = await getShowOtherUsersReviewEntities();
            const configValue = configResponse.data.show_other_users_review_entities;
            console.log('DEBUG: AddReviewEntity - show_other_users_review_entities config value:', configValue);
            setShowOtherUsersReviewEntities(configValue);
            
            // Then fetch entities based on config
            let response;
            if (configValue) {
                console.log('DEBUG: AddReviewEntity - Config is true, fetching all review entities with userId:', userId);
                response = await getReviewEntityByReviewee(userId);
            } else {
                console.log('DEBUG: AddReviewEntity - Config is false, fetching only my review entities');
                response = await getMyReviewEntities();
            }
            
            console.log('DEBUG: AddReviewEntity - Entity response received:', response);
            
            // Convert from API type to component type with proper type assertion
            const convertedEntities = response.data.map(entity => entity as unknown as ReviewEntity);
            setReviewEntities(convertedEntities);
        } catch (err) {
            console.error('DEBUG: AddReviewEntity - Error fetching review entities:', err);
            setError('Failed to load review entities');
        }
    };

    // Initialize user ID
    useEffect(() => {
        const user = getUser();
        if (user?.id) {
            console.log('DEBUG: AddReviewEntity - Setting userId to:', user.id);
            setUserId(user.id);
        }
    }, []);
    
    useEffect(() => {
        if (open && userId) {
            fetchUsers();
            fetchEntityTypes();
            fetchReviewEntities();
            // Fetch review definitions
            getReviewDefinitions().then(response => {
                setAllReviewDefinitions(response.data);
            }).catch(err => {
                console.error('Error fetching review definitions:', err);
            });
        }
    }, [open, userId]);

    const handleAddEntity = async () => {
        try {
            // Create payload with the required format
            const payload = {
                ...formData,
                parent_ids: formData.parent_ids || [] // Ensure parent_ids is included
            };
            
            await createReviewEntity(payload);
            resetForm();
            onSuccess();
        } catch (err) {
            console.error('Error creating review entity:', err);
            setError('Failed to create review entity');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            org_entity_code: '',
            reviewers: [],
            reviewees: [],
            data_entry_supports: [],
            owner: undefined,
            status: 'ACTIVE',
            valid_from: new Date().toISOString().split('T')[0], // Current date
            valid_till: '9999-12-31', // Infinity date
            review_entity_type_id: 0,
            parent_ids: [],
            review_definition_ids: []
        });
        setSelectedCategory('');
        setAvailableEntities([]);
        setError('');
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleMultiSelectChange = (event: SelectChangeEvent<any>, field: string) => {
        const { value } = event.target;
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            
            // If reviewees are being updated and there's at least one reviewee, set the first one as owner
            if (field === 'reviewees' && value.length > 0) {
                newData.owner = value[0];
            }
            
            return newData;
        });
    };
    
    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        const category = event.target.value;
        setSelectedCategory(category);
        
        // Update available entities for this category
        if (category && entityTypesByCategory[category]) {
            setAvailableEntities(entityTypesByCategory[category]);
        } else {
            setAvailableEntities([]);
        }
    };
    
    const handleEntityTypeSelect = (event: SelectChangeEvent<number>) => {
        const entityTypeId = Number(event.target.value);
        setFormData(prev => ({
            ...prev,
            review_entity_type_id: entityTypeId
        }));
    };
    
    const handleParentChange = (event: SelectChangeEvent<number[]>) => {
        const parentIds = event.target.value as number[];
        setFormData(prev => ({
            ...prev,
            parent_ids: parentIds
        }));
    };

    const getReviewerOptions = () => users.filter(user => 
        user.user_name !== 'SYSTEM_USER' && 
        user.roles.some((role: any) => role.role_name === 'REVIEWER')
    );

    const getRevieweeOptions = () => users.filter(user => 
        user.user_name !== 'SYSTEM_USER' && 
        user.roles.some((role: any) => role.role_name === 'REVIEWEE')
    );

    const getDataEntrySupportOptions = () => users.filter(user =>
        user.user_name !== 'SYSTEM_USER' &&
        user.roles.some((role: any) => role.role_name === 'DATA_ENTRY_SUPPORT')
    );

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Add Review Entity</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        name="name"
                        label="Name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Organization Entity Code"
                        name="org_entity_code"
                        value={formData.org_entity_code}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth required>
                        <InputLabel>Reviewers</InputLabel>
                        <Select
                            multiple
                            value={formData.reviewers}
                            onChange={(e) => handleMultiSelectChange(e, 'reviewers')}
                            input={<OutlinedInput label="Reviewers" />}
                        >
                            {getReviewerOptions().map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.display_name || user.user_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl fullWidth required>
                        <InputLabel>Reviewees</InputLabel>
                        <Select
                            multiple
                            value={formData.reviewees}
                            onChange={(e) => handleMultiSelectChange(e, 'reviewees')}
                            input={<OutlinedInput label="Reviewees" />}
                        >
                            {getRevieweeOptions().map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.display_name || user.user_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Data Entry Support</InputLabel>
                        <Select
                            multiple
                            value={formData.data_entry_supports}
                            onChange={(e) => handleMultiSelectChange(e, 'data_entry_supports')}
                            input={<OutlinedInput label="Data Entry Support" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as number[]).map((value) => {
                                        const user = users.find(u => u.id === value);
                                        return <Chip key={value} label={user?.display_name || user?.user_name || value} />;
                                    })}
                                </Box>
                            )}
                        >
                            {getDataEntrySupportOptions().map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.display_name || user.user_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Owner</InputLabel>
                        <Select
                            value={formData.owner || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value as number }))}
                            input={<OutlinedInput label="Owner" />}
                        >
                            <MenuItem value=""><em>Select an owner</em></MenuItem>
                            {getRevieweeOptions().map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.display_name || user.user_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    {/* Status Selection */}
                    <FormControl fullWidth required>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                            input={<OutlinedInput label="Status" />}
                        >
                            <MenuItem value="DRAFT">DRAFT</MenuItem>
                            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                            <MenuItem value="IN-ACTIVE">IN-ACTIVE</MenuItem>
                        </Select>
                    </FormControl>
                    
                    {/* Valid From Date */}
                    <TextField
                        fullWidth
                        label="Valid From"
                        name="valid_from"
                        type="date"
                        value={formData.valid_from}
                        onChange={handleChange}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        required
                    />
                    
                    {/* Valid Till Date */}
                    <TextField
                        fullWidth
                        label="Valid Till"
                        name="valid_till"
                        type="date"
                        value={formData.valid_till}
                        onChange={handleChange}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        required
                    />
                    
                    {/* Entity Type Category Selection */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Entity Type Category</InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            input={<OutlinedInput label="Entity Type Category" />}
                        >
                            <MenuItem value=""><em>Select a category</em></MenuItem>
                            {Object.keys(entityTypesByCategory).map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    {/* Entity Type Selection based on Category */}
                    <FormControl fullWidth required sx={{ mb: 2 }} disabled={!selectedCategory}>
                        <InputLabel>Entity Type</InputLabel>
                        <Select
                            value={formData.review_entity_type_id || ''}
                            onChange={handleEntityTypeSelect}
                            input={<OutlinedInput label="Entity Type" />}
                        >
                            <MenuItem value=""><em>Select an entity type</em></MenuItem>
                            {availableEntities.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.display_label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Parent Entity Selection */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Parent Entities</InputLabel>
                        <Select
                            multiple
                            value={formData.parent_ids}
                            onChange={handleParentChange}
                            input={<OutlinedInput label="Parent Entities" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const parent = reviewEntities.find(re => re.id === value);
                                        return (
                                            <Chip 
                                                key={value} 
                                                label={parent ? parent.name : `ID: ${value}`} 
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {reviewEntities.map((entity) => (
                                <MenuItem key={entity.id} value={entity.id}>
                                    {entity.name} ({entity.review_entity_type?.display_label || 'Unknown Type'})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Review Definitions Selection */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Review Definitions</InputLabel>
                        <Select
                            multiple
                            value={formData.review_definition_ids}
                            onChange={(e) => setFormData(prev => ({ ...prev, review_definition_ids: e.target.value as number[] }))}
                            input={<OutlinedInput label="Review Definitions" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const def = allReviewDefinitions.find(d => d.id === value);
                                        return (
                                            <Chip 
                                                key={value} 
                                                label={def?.display_label || def?.name || value}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {allReviewDefinitions.map((def) => (
                                <MenuItem key={def.id} value={def.id}>
                                    <Box>
                                        <Typography variant="body2">{def.display_label}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {def.review_frequency?.display_label} - {def.description}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
  <Button onClick={handleClose}>Cancel</Button>
  <Button
    variant="contained"
    color="primary"
    onClick={handleAddEntity}
    sx={{ textTransform: 'none' }}
  >
    Create
  </Button>
</DialogActions>

        </Dialog>
    );
};